/**
 * Unified API Client
 *
 * Axios-based HTTP client. Auth is handled via the __session cookie (sent
 * automatically with `withCredentials: true`); the browser never sees JWT
 * and there is no client-side refresh logic.
 *
 * Features:
 * - Automatic __session cookie inclusion (withCredentials)
 * - Centralised error transform (ApiError)
 * - 402 quota exceeded → CustomEvent for UpgradeModal
 * - Request/response logging in development
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios'
import { apiBase, apiUrl } from '../apiBase'

// =============================================================================
// Types
// =============================================================================

export interface ApiError {
  message: string
  code?: string
  status?: number
  details?: unknown
}

export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  success: boolean
}

// =============================================================================
// Axios Instance Creation
// =============================================================================

function createAxiosInstance(): AxiosInstance {
  return axios.create({
    baseURL: apiBase(),
    timeout: 30000,
    withCredentials: true, // send __session cookie on cross-origin requests
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  })
}

const apiClient = createAxiosInstance()

// =============================================================================
// Request Interceptor — dev logging only (no auth header injection)
// =============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (import.meta.env.DEV) {
      config.headers['X-Request-Time'] = new Date().toISOString()
    }
    return config
  },
  (error: AxiosError) => {
    if (import.meta.env.DEV) {
      console.error('[API Client] Request error:', error)
    }
    return Promise.reject(error)
  }
)

// =============================================================================
// Session probe — single-flight dedupe for concurrent 401s
// =============================================================================

let _sessionProbeInflight: Promise<void> | null = null
let _sessionProbeCooldownUntil = 0

/**
 * Hit GET /api/auth/me to confirm whether the session is alive after a 401
 * from a non-/me endpoint. Dispatches `auth:session-expired` only when /me
 * itself returns 401 (genuinely dead session). 200 means the prior 401 was
 * a business-level rejection — we do nothing.
 *
 * - Single-flight: concurrent business 401s share one probe.
 * - 10s cooldown after a successful "alive" verdict so a tab full of
 *   permission-gated requests doesn't probe N times per page.
 * - Uses native fetch (not axios) so the probe doesn't itself trigger
 *   the response interceptor and recurse.
 */
async function probeSessionAlive(): Promise<void> {
  if (typeof window === 'undefined') return
  const now = Date.now()
  if (now < _sessionProbeCooldownUntil) return
  if (_sessionProbeInflight) return _sessionProbeInflight

  _sessionProbeInflight = (async () => {
    try {
      const res = await fetch(apiUrl('/api/auth/me'), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (res.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
      } else if (res.ok) {
        // Session is alive — quiet for 10s before probing again
        _sessionProbeCooldownUntil = Date.now() + 10_000
      }
      // 5xx / network error → don't conclude anything; next 401 will retry
    } catch {
      // Network failure — leave session state alone
    } finally {
      _sessionProbeInflight = null
    }
  })()

  return _sessionProbeInflight
}

// =============================================================================
// Response Interceptor — error transform + quota event
// =============================================================================

apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`[API Client] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`)
    }
    return response
  },

  async (error: AxiosError<ApiError>) => {
    // 401 handling — probe-and-reconcile pattern.
    //
    // We CANNOT treat every 401 as session-expired: some endpoints (e.g.
    // /api/membership/status, paid-tier gates) legitimately return 401 to a
    // logged-in user without the right entitlement. Globally dispatching
    // session-expired on every 401 kicks the user out mid-session.
    //
    // Strategy: when a non-/me request 401s, probe GET /api/auth/me. If /me
    // is still 200 the session is alive — it was a business 401, do nothing
    // global. If /me is also 401 the session is genuinely dead — dispatch.
    // Concurrent 401s share a single probe via _sessionProbeInflight to
    // avoid 5-tab burst → 5 probe requests.
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const failingUrl = error.config?.url || ''
      const isMeProbe = /\/api\/auth\/me(\?|$)/.test(failingUrl)

      if (isMeProbe) {
        // /me itself 401 = session truly dead. No further probe needed.
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
      } else {
        // Business endpoint 401 — reconcile against /me before deciding.
        // Fire-and-forget; do not await before throwing the original error so
        // the caller's promise resolves on its normal timeline.
        void probeSessionAlive()
      }
    }

    // 402: quota exceeded — dispatch event for UpgradeModal
    if (error.response?.status === 402) {
      const detail = (error.response.data as unknown as Record<string, unknown>)?.detail
      if (detail && typeof detail === 'object') {
        window.dispatchEvent(new CustomEvent('quota:exceeded', { detail }))
      }
    }

    if (error.response) {
      const { status, data } = error.response

      if (import.meta.env.DEV) {
        console.error(`[API Client] Error ${status}:`, data)
      }

      const apiError: ApiError = {
        message: data?.message || getDefaultErrorMessage(status),
        code: data?.code || `HTTP_${status}`,
        status,
        details: data?.details,
      }

      return Promise.reject(apiError)
    }

    if (error.request) {
      const networkError: ApiError = {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      }
      return Promise.reject(networkError)
    }

    return Promise.reject({
      message: error.message || 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
    } as ApiError)
  }
)

function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Bad request. Please check your input.'
    case 401:
      return 'Authentication required. Please log in.'
    case 403:
      return 'Access denied. You do not have permission.'
    case 404:
      return 'Resource not found.'
    case 409:
      return 'Conflict. The resource already exists.'
    case 422:
      return 'Validation failed. Please check your input.'
    case 429:
      return 'Too many requests. Please try again later.'
    case 500:
      return 'Server error. Please try again later.'
    case 502:
      return 'Service temporarily unavailable.'
    case 503:
      return 'Service unavailable. Please try again later.'
    default:
      return `Request failed with status ${status}`
  }
}

// =============================================================================
// HTTP Methods Wrapper
// =============================================================================

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

export async function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

export async function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

export { apiClient }
export default apiClient
